/**
 * "Let's Reroll" — Reviews / FAQ / Screenshots as tabs (desktop) or accordions (mobile).
 *
 * WHY JAVASCRIPT AND NOT A WRAPPER SECTION
 * Shopify renders these as three independent sibling sections, and one of them is Junip's APP
 * section — a section of type `apps`, which cannot be rendered from inside a section of our own.
 * So the three stay exactly where they are in the template and this only adds a control surface
 * around them and toggles which is shown.
 *
 * JUNIP MUST NEVER START HIDDEN. It renders client-side, and hiding a client-rendered widget can
 * stop it initialising — the same trap as the product-recommendations IntersectionObserver, which
 * never fired because its element had no box. Reviews is therefore the default-open panel in BOTH
 * modes, so Junip is visible from the moment the page loads. FAQ and Screenshots are plain
 * server-rendered HTML and are safe to hide.
 *
 * NO-JS FALLBACK: the sections are also reordered in templates/product.json to match the tab order,
 * so if this file fails to load the page still reads Reviews → FAQ → Screenshots as three ordinary
 * stacked sections. Nothing is hidden until JS runs.
 *
 * TWO INDEPENDENT STATES, NOT ONE. Desktop shows exactly one panel; mobile lets any number be open
 * at once. Those can't share a single `hidden` attribute, so visibility is driven by two separate
 * classes — .lr-tabpanel--current for the tab view and .lr-tabpanel--open for the accordion view —
 * and the stylesheet decides which one matters at the current width. That means no resize handler
 * and no state to rebuild when someone rotates their phone.
 */
(function () {
  'use strict';

  var TAB_CLASS = 'lr-tabpanel--current';
  var ACC_CLASS = 'lr-tabpanel--open';

  function sectionOf(el) {
    return el ? el.closest('.shopify-section') : null;
  }

  /*
   * Count badge (.gacha-tabs-count in the handoff). Only rendered when a count can be determined
   * honestly — an absent badge is better than a wrong or "0" one.
   *  - FAQ: number of accordion items, known server-side and stable.
   *  - Screenshots: number of images the section actually rendered.
   *  - Reviews: Junip renders asynchronously and exposes no count at this point, so it gets no
   *    badge. Deliberately not guessed.
   */
  function countFor(label, el) {
    if (label === 'FAQ') return el.querySelectorAll('.chm-toggle').length || null;
    if (label === 'Screenshots') return el.querySelectorAll('.lr-proof__img').length || null;
    /*
     * Judge.me's preview badge renders the product's own count as text ("15 reviews"), so a count
     * is available here where Junip offered none. Parsed as a leading integer rather than by
     * scraping a fixed format, and discarded unless it's a real positive number — a missing badge
     * is better than "NaN" or a misleading 0.
     * The badge lives in the main product section, not in this panel, so it's looked up globally.
     */
    if (label === 'Reviews') {
      var badge = document.querySelector('.jdgm-preview-badge');
      if (!badge) return null;
      var n = parseInt((badge.textContent || '').replace(/[^0-9]/g, ''), 10);
      return (isFinite(n) && n > 0) ? n : null;
    }
    return null;
  }

  function collectPanels() {
    var panels = [];

    /*
     * The reviews section is an APP section, so its id is a random app id and can't be matched by
     * name the way the theme's own sections can. Match on the widget the app renders instead.
     *
     * BOTH APPS ARE LISTED. The store moved Junip -> Judge.me, and because this only knew Junip's
     * class names the Reviews tab silently disappeared. Keeping both means moving again, or back,
     * doesn't break it.
     *
     * Judge.me care: `.jdgm-review-widget` is the review LIST. It does not match
     * `.jdgm-review-widget-modal` (the write-a-review dialog) — that's a different class token, and
     * the modal sits outside any section anyway, so sectionOf() would discard it.
     * Junip care: `.junip-product-summary` is the star row inside the MAIN product section, so
     * matching it here would make the tab wrap the whole product. Hence the container/review
     * classes only.
     */
    var reviews = sectionOf(document.querySelector(
      '.jdgm-review-widget, .junip-reviews-container, .junip-product-review'
    ));
    if (reviews) panels.push({ label: 'Reviews', el: reviews });

    var faq = document.querySelector('[id^="shopify-section-"][id$="__faq"]');
    if (faq) panels.push({ label: 'FAQ', el: faq });

    /*
     * The screenshots section outputs nothing at all when no image carries the alt marker, but
     * Shopify still renders the empty <section> wrapper. Testing for the grid rather than the
     * section is what makes the tab appear only when there are actually screenshots.
     */
    var proof = sectionOf(document.querySelector('[data-section-name="proof-screenshots"] .lr-proof__grid'));
    if (proof) panels.push({ label: 'Screenshots', el: proof });

    return panels;
  }

  function build(panels) {
    var first = panels[0].el;

    var wrap = document.createElement('div');
    wrap.className = 'lr-tabs';

    var list = document.createElement('div');
    list.className = 'lr-tabs__list';
    list.setAttribute('role', 'tablist');
    wrap.appendChild(list);
    first.parentNode.insertBefore(wrap, first);

    panels.forEach(function (panel, i) {
      var id = panel.el.id || 'lr-panel-' + i;
      panel.el.id = id;
      var tabId = id + '-tab';

      var tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'lr-tabs__tab';
      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);
      tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      tab.tabIndex = i === 0 ? 0 : -1;
      var n = countFor(panel.label, panel.el);
      tab.appendChild(document.createTextNode(panel.label));
      if (n) {
        var c = document.createElement('span');
        c.className = 'lr-tabs__count';
        c.textContent = n;
        tab.appendChild(c);
      }
      list.appendChild(tab);

      /* Accordion header for the mobile view, placed immediately before its own section. */
      var head = document.createElement('button');
      head.type = 'button';
      head.className = 'lr-acc__head';
      head.setAttribute('aria-controls', id);
      head.setAttribute('aria-expanded', i === 0 ? 'true' : 'false');
      head.innerHTML = '<span>' + panel.label + '</span>' +
        (n ? '<span class="lr-acc__count">' + n + '</span>' : '') +
        '<span class="lr-acc__icon" aria-hidden="true"></span>';
      if (i === 0) head.classList.add('lr-acc__head--first');
      panel.el.parentNode.insertBefore(head, panel.el);

      panel.el.classList.add('lr-tabpanel');
      panel.el.setAttribute('role', 'tabpanel');
      panel.el.setAttribute('aria-labelledby', tabId);
      if (i === 0) {
        panel.el.classList.add(TAB_CLASS);
        panel.el.classList.add(ACC_CLASS);
      }

      panel.tab = tab;
      panel.head = head;

      tab.addEventListener('click', function () { selectTab(panels, i); });
      head.addEventListener('click', function () { toggleAccordion(panel); });
    });

    /* Empty filler so the 2px rule continues to the full width past the last tab — the handoff
       does this with a 4th grid column. aria-hidden: it is decoration, not a tab. */
    var fill = document.createElement('span');
    fill.className = 'lr-tabs__fill';
    fill.setAttribute('aria-hidden', 'true');
    list.appendChild(fill);

    /* Left/right arrows move between tabs, which is the expected behaviour for a tablist. */
    list.addEventListener('keydown', function (e) {
      var current = panels.findIndex(function (p) { return p.tab === document.activeElement; });
      if (current === -1) return;
      var next = null;
      if (e.key === 'ArrowRight') next = (current + 1) % panels.length;
      if (e.key === 'ArrowLeft') next = (current - 1 + panels.length) % panels.length;
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = panels.length - 1;
      if (next === null) return;
      e.preventDefault();
      selectTab(panels, next);
      panels[next].tab.focus();
    });
  }

  function selectTab(panels, index) {
    panels.forEach(function (p, i) {
      var on = i === index;
      p.tab.setAttribute('aria-selected', on ? 'true' : 'false');
      p.tab.tabIndex = on ? 0 : -1;
      p.el.classList.toggle(TAB_CLASS, on);
    });
  }

  function toggleAccordion(panel) {
    var open = panel.el.classList.toggle(ACC_CLASS);
    panel.head.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function init() {
    var panels = collectPanels();
    /* One panel is not a tab set - leave the page exactly as it is. */
    if (panels.length < 2) return;
    build(panels);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
